<template>
  <div class="main-container">
    <!-- 左侧菜单区（包含顶部头像） -->
    <div class="sidebar" v-if="!isMobile">
      <UserProfile
        :userDetail="userDetail"
        @change-password="showPasswordDialog = true"
        @logout="logout"
        @fetch-user-detail="fetchUserDetail"
      />
      <!-- PC 菜单列表 -->
      <SideMenu :menus="filteredMenus" v-if="!isMobile" />
    </div>
    <el-drawer
      :visible="showMobileMenu"
      direction="ltr"
      size="50%"
      @close="showMobileMenu = false"
    >
      <UserProfile
        :userDetail="userDetail"
        @change-password="(showPasswordDialog = true), (showMobileMenu = false)"
        @logout="logout"
        @fetch-user-detail="fetchUserDetail"
      />
      <SideMenu :menus="filteredMenus" v-if="isMobile" />
    </el-drawer>

    <!-- 右侧内容区 -->
    <div class="content">
      <div v-if="isMobile" class="mobile-menu">
        <i
          class="el-icon-menu"
          @click="showMobileMenu = isMobile && !showMobileMenu"
        ></i>
      </div>
      <router-view />
    </div>
    <!-- 密码修改弹窗组件 -->
    <PasswordDialog
      :visible.sync="showPasswordDialog"
      @close="handleDialogClose"
    />
  </div>
</template>
<script>
import PasswordDialog from "@/components/PasswordDialog.vue";
import { Role } from "@/constants/Role";
import { responsiveMixin } from "@/mixins/responsiveMixin";
import router from "@/router";
import SideMenu from "./SideMenu.vue";
import UserProfile from "./UserProfile.vue";

function getMatchedRoute(name) {
  const matched = router.getRoutes().find((route) => route.name === name);
  return matched;
}

function getRequiredRole(name) {
  const matched = getMatchedRoute(name);
  return matched?.meta?.requiredRole || [];
}

// axios已在main.js中全局配置，使用this.$http代替直接导入的axios
export default {
  mixins: [responsiveMixin],
  name: "MainPage",
  components: { PasswordDialog, SideMenu, UserProfile },
  data() {
    return {
      userDetail: {
        name: "",
        phone: "",
        department: "",
      },
      showUserDetail: false,
      showUserMenu: false,
      showPasswordDialog: false,
      showMobileMenu: false,
      menus: [
        {
          title: "绩效考核",
          icon: "el-icon-data-line",
          expanded: false,
          children: [
            {
              title: "绩效管理",
              icon: "el-icon-tickets",
              expanded: false,
              children: [
                {
                  title: "管理关键指标",
                  icon: "el-icon-setting",
                  requiredRole: [Role.SUPER],
                  routeName: "CategoryManage",
                },
                {
                  title: "固定考核规则管理",
                  icon: "el-icon-lock",
                  requiredRole: [Role.SUPER],
                  routeName: "FixedDimensionManage",
                },
                {
                  title: "考核规则",
                  icon: "el-icon-document",
                  routeName: "AssessmentRule",
                },
                {
                  title: "考核管理",
                  icon: "el-icon-collection",
                  routeName: "AssessmentTemplate",
                },
                {
                  title: "我的绩效",
                  icon: "el-icon-notebook-1",
                  routeName: "AssessmentRecord",
                },
              ],
            },
            {
              title: "绩效分析",
              icon: "el-icon-data-analysis",
              requiredRole: [Role.ADMIN, Role.SUPER],
              expanded: false,
              children: [
                {
                  title: "员工分析",
                  icon: "el-icon-pie-chart",
                  requiredRole: getRequiredRole("DepartmentPerformance"),
                  hidden: true,
                  routeName: "DepartmentPerformance",
                },
                {
                  title: "绩效档案",
                  icon: "el-icon-user",
                  requiredRole: getRequiredRole("PersonalPerformance"),
                  routeName: "PersonalPerformance",
                },
                {
                  title: "部门绩效",
                  icon: "el-icon-office-building",
                  requiredRole: getRequiredRole("DepartmentPerformancePage"),
                  routeName: "DepartmentPerformancePage",
                },
              ],
            },
          ],
        },
        {
          title: "企业培训",
          icon: "el-icon-reading",
          expanded: false,
          children: [
            {
              title: "课程管理",
              icon: "el-icon-collection-tag",
              requiredRole: getRequiredRole("CourseManager"),
              routeName: "CourseManager",
            },
            {
              title: "学习记录",
              icon: "el-icon-timer",
              requiredRole: getRequiredRole("LearningRecord"),
              routeName: "LearningRecord",
            },
            {
              title: "我的培训",
              icon: "el-icon-user",
              routeName: "MyTraining",
            },
          ],
        },
        {
          title: "人事档案",
          icon: "el-icon-user-solid",
          expanded: false,
          requiredRole: [Role.ADMIN],
          children: [
            {
              title: "花名册",
              icon: "el-icon-s-custom",
              requiredRole: getRequiredRole("EmployeeList"),
              routeName: "EmployeeList",
            },
            {
              title: "档案分析",
              icon: "el-icon-data-analysis",
              requiredRole: getRequiredRole("StaffStatistics"),
              routeName: "StaffStatistics",
            },
          ],
        },
      ],
    };
  },
  computed: {
    userRole() {
      return this.$store.getters['user/getUserRole'];
    },
    filteredMenus() {
      const currentRole = this.userRole;

      // 关键修复：直接使用原始菜单数据，避免深拷贝破坏响应式
      const filterMenu = (menu) => {
        if (menu.hidden) {
          return false;
        }
        // 特殊处理super角色：隐藏人事档案主菜单
        if (currentRole === Role.SUPER && menu.title === "人事档案") {
          return false;
        }
        if (menu.requiredRole && !menu.requiredRole.includes(currentRole)) {
          return false;
        }
        // 过滤DingTalk用户的菜单
        if (menu.children) {
          menu.children = menu.children.filter((child) => filterMenu(child));
          return menu.children.length > 0;
        }

        return true;
      };

      // 创建浅拷贝，保留响应式特性
      return [...this.menus].filter((menu) => filterMenu(menu));
    },
  },
  mounted() {
    //console.log('- 从localStorage获取的角色:', newRole)
    console.log("- 更新前 userRole:", this.userRole);

    // 根据用户角色自动跳转到对应的默认页面
    this.navigateByRole();
  },
  methods: {
    navigateByRole() {
      // 确保只在首次加载且没有指定路由时进行跳转
      if (
        this.$route.path === "/" ||
        this.$route.path === "/main" ||
        this.$route.path === "/employee-list"
      ) {
        if (this.userRole === Role.ADMIN) {
          // 管理员角色跳转到员工列表
          this.$router.replace({ name: "EmployeeList" }).catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("导航错误:", err);
            }
          });
        } else if (this.userRole === Role.USER) {
          // 普通用户角色跳转到考核规则
          this.$router.replace({ name: "AssessmentRule" }).catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("导航错误:", err);
            }
          });
        } else if (this.userRole === Role.SUPER) {
          // super角色跳转到考核规则
          this.$router.replace({ name: "AssessmentRule" }).catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("导航错误:", err);
            }
          });
        }
      }
    },
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
      if (this.showUserDetail) {
        this.showUserDetail = false;
      }
    },
    showChangePassword() {
      this.showPasswordDialog = true;
      this.showUserMenu = false;
    },
    handleDialogClose() {
      this.showPasswordDialog = false;
    },
    showUserInfo() {
      this.showUserDetail = true;
      this.showUserMenu = false;
      this.fetchUserDetail();
    },
    closeUserDetail() {
      this.showUserDetail = false;
    },
    async fetchUserDetail() {
      try {
        // 从store获取登录时使用的账号或手机号
        const account = this.$store.getters['user/getUserName'];
        const phone = this.$store.getters['user/getCurrentUser']?.phone || '';
        console.log("信息如下输出：");
        console.log(account);
        console.log(phone);

        let response;

        // 条件判断：优先使用账号查询，其次使用手机号查询
        if (account && account.trim() !== "") {
          console.log("使用账号查询流程:", account);

          // 第一步：通过账号获取手机号
          const phoneResponse = await this.$http({
            method: "get",
            url: "/info/byName",
            params: {
              name: account.trim(),
            },
            timeout: 10000,
          });

          // 验证第一步响应
          if (
            phoneResponse &&
            phoneResponse.status === 200 &&
            phoneResponse.data
          ) {
            const userPhone =
              phoneResponse.data.phone || phoneResponse.data.telphone;
            console.log("获取到的手机号:", userPhone);

            if (userPhone && userPhone.trim() !== "") {
              // 第二步：使用手机号获取员工详细信息
              response = await this.$http({
                method: "get",
                url: "/staff/info/byPhone",
                params: {
                  phone: userPhone.trim(),
                },
                timeout: 10000,
              });
            } else {
              console.error("第一步获取手机号失败");
              this.$message.error("获取用户手机号失败");
              return;
            }
          } else {
            this.$message.error("获取用户手机号失败: 接口响应异常");
            return;
          }
        } else if (phone && phone.trim() !== "") {
          // 直接使用手机号查询用户信息（原有逻辑保持不变）
          console.log("使用手机号直接查询:", phone);
          response = await this.$http({
            method: "get",
            url: "/staff/info/byPhone",
            params: {
              phone: phone.trim(),
            },
            timeout: 10000,
          });
        } else {
          this.$message.error("未找到用户登录信息");
          return;
        }

        // 增强数据验证
        if (response && response.status === 200 && response.data) {
          this.userDetail = {
            name: response.data.name || "未知",
            phone: response.data.phone || "未知",
            department: response.data.department || "未知部门",
          };
          this.$message.success("获取用户信息成功");
        } else {
          this.$message.error("获取用户信息失败: 响应数据异常");
        }
      } catch (error) {
        console.error("获取用户信息失败:", error);

        // 更详细的错误处理
        if (error.response) {
          switch (error.response.status) {
            case 404:
              this.$message.error("用户不存在或接口路径错误");
              break;
            case 400:
              this.$message.error("请求参数错误，请检查参数格式");
              break;
            default:
              this.$message.error(`请求失败: ${error.response.status}`);
          }
        } else if (error.code === "ECONNABORTED") {
          this.$message.error("请求超时，请检查网络连接");
        } else {
          this.$message.error("网络连接失败，请检查服务端是否启动");
        }
      }
    },
    logout() {
      // 退出登录逻辑
      this.$confirm("确定要退出登录吗?", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        // 清除所有用户相关的本地存储信息
        this.$store.dispatch('user/clearCurrentUser');

        // 清除sessionStorage中的token
        sessionStorage.removeItem("token");

        // 跳转到登录页面
        this.$router
          .push("/login")
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.error("导航错误:", err);
            }
          })
          .finally(() => {
            this.showUserMenu = false;
          });
      });
    },
  },
};
</script>

<style scoped>
.main-container {
  height: 100vh;
  display: flex;
}

.mobile-menu {
  padding: 1rem;
  padding-bottom: 0;
}
.sidebar {
  width: 250px;
  background: #fff;
  border-right: 1px solid #e6e6e6;
  display: flex;
  flex-direction: column;
}

.user-profile {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  text-align: center;
}

.avatar-container {
  display: inline-block;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid #409eff;
}

.user-menu {
  position: absolute;
  top: 120px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  z-index: 101;
}

.menu-item {
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.menu-item i {
  margin-right: 8px;
}

.menu-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
}

.menu-group {
  margin-bottom: 5px;
}

.menu-title {
  padding: 12px 20px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.menu-title:hover {
  background-color: #f5f7fa;
}

.menu-icon {
  margin-right: 10px;
  font-size: 16px;
  color: #409eff;
}

.arrow {
  border: solid #333;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  margin-left: auto;
  transition: transform 0.3s;
}

.arrow.right {
  transform: rotate(-45deg);
}

.arrow.down {
  transform: rotate(45deg);
}

.submenu-group {
  background-color: #fafafa;
}

.submenu-item {
  padding: 10px 20px 10px 35px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.submenu-item:hover {
  background-color: #f0f7ff;
}

.submenu-icon {
  margin-right: 8px;
  font-size: 14px;
  color: #67c23a;
}

.thirdmenu-group {
  background-color: #f5f5f5;
}

.thirdmenu-item {
  padding: 8px 20px 8px 50px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.thirdmenu-item:hover {
  background-color: #ebf5ff;
}

.thirdmenu-icon {
  margin-right: 8px;
  font-size: 14px;
  color: #e6a23c;
}

.content {
  flex: 1;
  overflow-y: auto;
  height: 100vh;
}

.new-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 0 5px;
  font-size: 12px;
  line-height: 16px;
  color: #fff;
  background-color: #f56c6c;
  border-radius: 10px;
  transform: scale(0.8);
}

.user-detail-panel {
  position: absolute;
  top: 140px;
  left: 180px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1001;
  width: 260px;
  animation: slideInRight 0.3s ease;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}

.detail-content {
  padding: 16px;
}

.info-value {
  color: #303133;
  font-weight: 400;
}

.info-row {
  display: flex;
  margin-bottom: 12px;
  align-items: center;
}

.info-label {
  color: #606266;
  font-weight: 500;
  min-width: 50px;
}

.user-detail-panel {
  position: absolute;
  top: 140px;
  left: 180px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1001;
  width: 260px;
  animation: slideInRight 0.3s ease;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}

.detail-header h3 {
  margin: 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.close-icon {
  cursor: pointer;
  color: #c0c4cc;
}

.close-icon:hover {
  color: #409eff;
}
</style>
